import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Container from './ui/Container';
import SectionHeading from './ui/SectionHeading';
import { Button } from './ui/Button';
import Card from './ui/Card';
import { useT, useLang } from '../i18n';

const T = {
  es: {
    notifications: {
      rateLimit: 'Por favor, espere unos segundos antes de enviar otro mensaje.',
      minTime: 'Por favor, tómese un momento para revisar el formulario.',
      tooManyAttempts: 'Demasiados intentos. Por favor, recargue la página.',
      success: '¡Gracias! Nos pondremos en contacto con usted pronto.',
      error: 'Error al enviar el formulario. Por favor, inténtelo de nuevo.',
    },
    badge: 'CONTACTO',
    headingLine1: 'Solicite su Demo',
    headingLine2: 'Personalizada',
    subheading: 'Sesión gratuita adaptada a su industria y operativa.',
    bullets: {
      noCommitment: 'Sin compromiso',
      relevantInfo: 'Información relevante',
      realCases: 'Casos reales',
    },
    platformHeading: 'Plataforma modular de tres productos',
    selectedInterests: 'Áreas de interés seleccionadas',
    products: {
      visibility: {
        title: 'Cadenia Visibility',
        description: 'Trazabilidad de envíos, seguimiento en tiempo real y gestión proactiva de incidencias',
      },
      intelligence: {
        title: 'Cadenia Intelligence',
        description: 'Analytics de supply chain, agentes de IA e informes automáticos para tomar mejores decisiones',
      },
      documents: {
        title: 'Cadenia Documents',
        description: 'Documento de control, carta de porte y eCMR con firma electrónica y cumplimiento normativo',
      },
    },
    process: {
      heading: 'Proceso Simple',
      steps: [
        {
          title: 'Análisis de Necesidades',
          description: 'Evaluamos sus desafíos logísticos actuales',
        },
        {
          title: 'Demo Personalizada',
          description: 'Presentación adaptada a su industria',
        },
        {
          title: 'Propuesta Comercial',
          description: 'Con la configuración recomendada y plan de implementación',
        },
      ],
    },
    form: {
      heading: 'Contacte con Cadenia',
      nameLabel: 'Nombre Completo *',
      namePlaceholder: 'Nombre',
      emailLabel: 'Email de trabajo *',
      emailPlaceholder: 'Email',
      companyLabel: 'Empresa *',
      companyPlaceholder: 'Empresa',
      phoneLabel: 'Teléfono',
      phonePlaceholder: 'Teléfono',
      messageLabel: 'Mensaje',
      messagePlaceholder: 'Mensaje',
      consent: 'Doy el consentimiento para tratar mis datos con el fin de responder a mi solicitud',
      submitting: 'Enviando...',
      submit: 'Solicitar Demo Gratuita',
    },
  },
  en: {
    notifications: {
      rateLimit: 'Please wait a few seconds before sending another message.',
      minTime: 'Please take a moment to review the form.',
      tooManyAttempts: 'Too many attempts. Please reload the page.',
      success: 'Thank you! We will be in touch with you shortly.',
      error: 'There was an error sending the form. Please try again.',
    },
    badge: 'CONTACT',
    headingLine1: 'Request Your',
    headingLine2: 'Personalized Demo',
    subheading: 'A free session tailored to your industry and operations.',
    bullets: {
      noCommitment: 'No commitment',
      relevantInfo: 'Relevant insights',
      realCases: 'Real-world cases',
    },
    platformHeading: 'A modular platform of three products',
    selectedInterests: 'Selected areas of interest',
    products: {
      visibility: {
        title: 'Cadenia Visibility',
        description: 'Shipment traceability, real-time tracking and proactive incident management',
      },
      intelligence: {
        title: 'Cadenia Intelligence',
        description: 'Supply chain analytics, AI agents and automated reports for better decision-making',
      },
      documents: {
        title: 'Cadenia Documents',
        description: 'Control document, consignment note and eCMR with electronic signature and regulatory compliance',
      },
    },
    process: {
      heading: 'A Simple Process',
      steps: [
        {
          title: 'Needs Analysis',
          description: 'We assess your current logistics challenges',
        },
        {
          title: 'Personalized Demo',
          description: 'A walkthrough tailored to your industry',
        },
        {
          title: 'Commercial Proposal',
          description: 'With the recommended configuration and implementation plan',
        },
      ],
    },
    form: {
      heading: 'Get in touch with Cadenia',
      nameLabel: 'Full Name *',
      namePlaceholder: 'Name',
      emailLabel: 'Work email *',
      emailPlaceholder: 'Email',
      companyLabel: 'Company *',
      companyPlaceholder: 'Company',
      phoneLabel: 'Phone',
      phonePlaceholder: 'Phone',
      messageLabel: 'Message',
      messagePlaceholder: 'Message',
      consent: 'I consent to the processing of my data for the purpose of responding to my request',
      submitting: 'Sending...',
      submit: 'Request a Free Demo',
    },
  },
};

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    message: '',
    consent: false,
    honeypot: ''
  });
  const [timestamp] = useState(Date.now());
  const [submitAttempts, setSubmitAttempts] = useState(0);
  const [lastSubmit, setLastSubmit] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [notification, setNotification] = useState(null);
  const [interests, setInterests] = useState([]);
  const t = useT(T);
  const { path } = useLang();

  useEffect(() => {
    // Obtener intereses de URL params si vienen de una página de producto
    const urlParams = new URLSearchParams(window.location.search);
    const urlInterests = urlParams.get('interests');
    if (urlInterests) {
      setInterests(urlInterests.split(','));
    }
  }, []);

  const handleChange = (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setFormData({
      ...formData,
      [e.target.name]: value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Protecciones básicas
    const now = Date.now();
    const timeSinceLoad = now - timestamp;
    const timeSinceLastSubmit = now - lastSubmit;

    // Honeypot check
    if (formData.honeypot) {
      return; // Bot detectado
    }

    // Rate limiting
    if (timeSinceLastSubmit < 5000) {
      setNotification({ type: 'warning', message: t.notifications.rateLimit });
      return;
    }

    // Tiempo mínimo en página
    if (timeSinceLoad < 3000) {
      setNotification({ type: 'warning', message: t.notifications.minTime });
      return;
    }

    // Límite de intentos
    if (submitAttempts >= 3) {
      setNotification({ type: 'error', message: t.notifications.tooManyAttempts });
      return;
    }

    setIsSubmitting(true);
    setSubmitAttempts(prev => prev + 1);
    setLastSubmit(now);

    try {
      const response = await fetch('https://a8e28svmy6.execute-api.eu-west-3.amazonaws.com/prod/contact', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': 'ELZglGzAcUaeexE0s2MmPhaWnRlOTN6H5YU8GY10'
        },
        body: JSON.stringify({
          ...formData,
          interests: interests.join(', '),
          timestamp,
          timeSinceLoad,
          source: 'contact-page'
        })
      });

      if (response.ok) {
        setNotification({ type: 'success', message: t.notifications.success });
        setFormData({ name: '', email: '', company: '', phone: '', message: '', consent: false, honeypot: '' });
        setInterests([]);
      } else {
        throw new Error('Error al enviar');
      }
    } catch (error) {
      setNotification({ type: 'error', message: t.notifications.error });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contacto" className="py-32 bg-gradient-to-br from-slate-50 to-blue-50 relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-blue-200 to-transparent" />
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_30%,rgba(59,130,246,0.05),transparent_50%)]" />

      <Container>
        <div className="text-center mb-20">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-50 border border-blue-100 rounded-full text-sm font-medium text-blue-700 mb-8">
            <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
            {t.badge}
          </div>
          <h2 className="text-4xl md:text-5xl font-light text-slate-900 mb-6">
            {t.headingLine1}
            <span className="block bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent font-medium">
              {t.headingLine2}
            </span>
          </h2>
          <p className="text-xl text-slate-600 max-w-3xl mx-auto leading-relaxed mb-8">
            {t.subheading}
          </p>

          <div className="flex flex-wrap justify-center gap-8">
            <div className="flex items-center gap-2 text-sm text-slate-600">
              <div className="w-2 h-2 bg-green-500 rounded-full"></div>
              {t.bullets.noCommitment}
            </div>
            <div className="flex items-center gap-2 text-sm text-slate-600">
              <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
              {t.bullets.relevantInfo}
            </div>
            <div className="flex items-center gap-2 text-sm text-slate-600">
              <div className="w-2 h-2 bg-indigo-500 rounded-full"></div>
              {t.bullets.realCases}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-20 max-w-7xl mx-auto">
          {/* Información de la demo */}
          <div className="relative">
            <div className="absolute -top-4 -left-4 w-24 h-24 bg-blue-100 rounded-full opacity-20"></div>
            <h3 className="text-3xl font-light text-slate-900 mb-10 relative">{t.platformHeading}</h3>

            {interests.length > 0 && (
              <Card className="mb-10 bg-primary-50/60 border-primary-100">
                <h4 className="text-sm font-medium text-primary-700 mb-4 tracking-wide uppercase">{t.selectedInterests}</h4>
                <div className="flex flex-wrap gap-2">
                  {interests.map((interest, index) => (
                    <span key={index} className="px-3 py-1 bg-primary-100 text-primary-800 text-xs font-medium rounded-full">
                      {interest.trim()}
                    </span>
                  ))}
                </div>
              </Card>
            )}

            <div className="space-y-6 mb-14">
              <Link to={path('visibility')} className="flex items-start p-3 -m-3 rounded-xl hover:bg-blue-50/50 transition-colors duration-200 group">
                <div className="w-9 h-9 border border-neutral-300 rounded-lg flex items-center justify-center text-neutral-600 mr-4 flex-shrink-0 bg-white shadow-sm group-hover:border-blue-300 group-hover:text-blue-600 transition-colors">
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                </div>
                <div>
                  <h4 className="font-medium text-neutral-900 mb-1 group-hover:text-blue-700 transition-colors">{t.products.visibility.title}</h4>
                  <p className="text-sm text-neutral-600">{t.products.visibility.description}</p>
                </div>
              </Link>

              <Link to={path('intelligence')} className="flex items-start p-3 -m-3 rounded-xl hover:bg-indigo-50/50 transition-colors duration-200 group">
                <div className="w-9 h-9 border border-neutral-300 rounded-lg flex items-center justify-center text-neutral-600 mr-4 flex-shrink-0 bg-white shadow-sm group-hover:border-indigo-300 group-hover:text-indigo-600 transition-colors">
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                  </svg>
                </div>
                <div>
                  <h4 className="font-medium text-neutral-900 mb-1 group-hover:text-indigo-700 transition-colors">{t.products.intelligence.title}</h4>
                  <p className="text-sm text-neutral-600">{t.products.intelligence.description}</p>
                </div>
              </Link>

              <Link to={path('documents')} className="flex items-start p-3 -m-3 rounded-xl hover:bg-emerald-50/50 transition-colors duration-200 group">
                <div className="w-9 h-9 border border-neutral-300 rounded-lg flex items-center justify-center text-neutral-600 mr-4 flex-shrink-0 bg-white shadow-sm group-hover:border-emerald-300 group-hover:text-emerald-600 transition-colors">
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <div>
                  <h4 className="font-medium text-neutral-900 mb-1 group-hover:text-emerald-700 transition-colors">{t.products.documents.title}</h4>
                  <p className="text-sm text-neutral-600">{t.products.documents.description}</p>
                </div>
              </Link>
            </div>

            <Card className="p-8">
              <h4 className="font-light text-neutral-900 mb-6">{t.process.heading}</h4>
              <div className="space-y-7">
                {t.process.steps.map((step, index) => (
                  <div key={index} className="flex items-start">
                    <div className="w-8 h-8 rounded-full flex items-center justify-center text-white text-[11px] font-medium mr-4 flex-shrink-0 bg-primary-600">{index + 1}</div>
                    <div>
                      <h5 className="font-medium text-neutral-900 mb-1">{step.title}</h5>
                      <p className="text-sm text-neutral-600">{step.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          </div>

          {/* Formulario de contacto */}
          <div className="relative">
            <div className="absolute -top-6 -right-6 w-32 h-32 bg-gradient-to-br from-blue-100 to-indigo-100 rounded-full opacity-30"></div>
            <div className="relative bg-white rounded-2xl p-10 shadow-xl border border-slate-100 lg:sticky lg:top-28">
              <h3 className="text-2xl font-light text-slate-900 mb-10">{t.form.heading}</h3>

              <form onSubmit={handleSubmit}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                <div>
                  <label htmlFor="name" className="block text-[11px] text-neutral-500 font-medium uppercase tracking-wide mb-2">
                    {t.form.nameLabel}
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    required
                    value={formData.name}
                    onChange={handleChange}
                    placeholder={t.form.namePlaceholder}
                    className="w-full px-0 py-3 border-0 border-b border-neutral-300 bg-transparent focus:outline-none focus:border-primary-600 transition-colors"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-[11px] text-neutral-500 font-medium uppercase tracking-wide mb-2">
                    {t.form.emailLabel}
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    placeholder={t.form.emailPlaceholder}
                    className="w-full px-0 py-3 border-0 border-b border-neutral-300 bg-transparent focus:outline-none focus:border-primary-600 transition-colors"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                <div>
                  <label htmlFor="company" className="block text-[11px] text-neutral-500 font-medium uppercase tracking-wide mb-2">
                    {t.form.companyLabel}
                  </label>
                  <input
                    type="text"
                    id="company"
                    name="company"
                    required
                    value={formData.company}
                    onChange={handleChange}
                    placeholder={t.form.companyPlaceholder}
                    className="w-full px-0 py-3 border-0 border-b border-neutral-300 bg-transparent focus:outline-none focus:border-primary-600 transition-colors"
                  />
                </div>
                <div>
                  <label htmlFor="phone" className="block text-[11px] text-neutral-500 font-medium uppercase tracking-wide mb-2">
                    {t.form.phoneLabel}
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder={t.form.phonePlaceholder}
                    className="w-full px-0 py-3 border-0 border-b border-neutral-300 bg-transparent focus:outline-none focus:border-primary-600 transition-colors"
                  />
                </div>
              </div>

              {/* Honeypot field */}
              <input
                type="text"
                name="honeypot"
                value={formData.honeypot}
                onChange={handleChange}
                style={{position: 'absolute', left: '-9999px', opacity: 0}}
                tabIndex="-1"
                autoComplete="off"
              />

              <div className="mb-8">
                <label htmlFor="message" className="block text-[11px] text-neutral-500 font-medium uppercase tracking-wide mb-2">
                  {t.form.messageLabel}
                </label>
                <textarea
                  id="message"
                  name="message"
                  rows="4"
                  value={formData.message}
                  onChange={handleChange}
                  placeholder={t.form.messagePlaceholder}
                  className="w-full px-0 py-3 border-0 border-b border-neutral-300 bg-transparent focus:outline-none focus:border-primary-600 transition-colors resize-none"
                ></textarea>
              </div>

              <div className="mb-8">
                <label className="flex items-start cursor-pointer select-none">
                  <input
                    type="checkbox"
                    name="consent"
                    checked={formData.consent}
                    onChange={handleChange}
                    required
                    className="mt-1 mr-3 h-4 w-4 text-primary-600 focus:ring-primary-500 border-neutral-300 rounded"
                  />
                  <span className="text-[11px] leading-relaxed text-neutral-600">
                    {t.form.consent}
                  </span>
                </label>
              </div>
              {notification && (
                <div className={`p-4 rounded-lg ${
                  notification.type === 'success' ? 'bg-green-50 text-green-800 border border-green-200' :
                  notification.type === 'error' ? 'bg-red-50 text-red-800 border border-red-200' :
                  'bg-yellow-50 text-yellow-800 border border-yellow-200'
                }`}>
                  <div className="flex justify-between items-start">
                    <p className="text-sm">{notification.message}</p>
                    <button
                      onClick={() => setNotification(null)}
                      className="ml-4 text-gray-400 hover:text-gray-600"
                    >
                      ×
                    </button>
                  </div>
                </div>
              )}

              <Button type="submit" disabled={isSubmitting} className="w-full h-14 text-sm bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200">
                {isSubmitting ? t.form.submitting : t.form.submit}
              </Button>
              </form>

            </div>
          </div>
        </div>
      </Container>
    </section>
  );
};

export default Contact;